import {
  buildProps,
  definePropType,
  iconPropType,
} from '@pit-element-plus/utils'
import type {
  CheckedInfo,
  TreeNode,
  TreeNodeData,
} from '@pit-element-plus/components/tree-v2/src/types'
import type { ExtractPropTypes } from 'vue'
import type LayoutTree from './layout-tree.vue'
import type { Placement } from '@pit-element-plus/components/popper'

export const layoutTreeProps = buildProps({
  /**
   * @description dataList
   */
  dataList: {
    type: Array,
    default: () => [],
  },
  /**
   * @description defaultObjectData
   */
  defaultObjectData: Object,
  /**
   * @description nodeKey
   */
  nodeKey: {
    type: String,
    default: 'id',
  },
  /**
   * @description queryTreeInterface
   */
  queryTreeInterface: String,
  /**
   * @description queryTreeInterfaceMethod
   */
  queryTreeInterfaceMethod: {
    type: String,
    default: 'GET',
  },
  /**
   * @description queryTreeParams
   */
  queryTreeParams: Object,
  /**
   * @description expandOnClickNode
   */
  expandOnClickNode: {
    type: Boolean,
    default: false,
  },
  /**
   * @description defaultExpandAll
   */
  defaultExpandAll: {
    type: Boolean,
    default: true,
  },
  /**
   * @description label
   */
  label: {
    type: String,
    default: 'label',
  },
  /**
   * @description isTooltip
   */
  isTooltip: {
    type: Boolean,
    default: true,
  },
  /**
   * @description placement
   */
  placement: {
    type: definePropType<Placement>(String),
    default: 'top',
  },
  /**
   * @description icon
   */
  icon: {
    type: iconPropType,
  },
  /**
   * @description iconColor
   */
  iconColor: {
    type: String,
    default: '',
  },
  /**
   * @description noSearchInput 是否显示输入框
   */
  noSearchInput: {
    type: Boolean,
    default: false,
  },
  /**
   * @description filterPlaceholder 输入框占位符
   */
  filterPlaceholder: {
    type: String,
    default: '请输入名称回车键查询',
  },
  /**
   * @description noFilterInput 执行input事件直接查询
   */
  noFilterInput: {
    type: Boolean,
    default: false,
  },
  /**
   * @description isRequestQuery
   */
  // 是否使用服务端查询
  isRequestQuery: {
    type: Boolean,
    default: false,
  },
  /**
   * @description beforeSearchCallback
   */
  // 输入框事件前回调
  beforeSearchCallback: Function,
  /**
   * @description renderContent 树节点的内容区的渲染 Function
   */
  renderContent: Function,
  /**
   * @description clearable
   */
  clearable: {
    type: Boolean,
    default: true,
  },
  /**
   * @description lazy 是否懒加载子节点数据
   */
  lazy: {
    type: Boolean,
    default: false,
  },
  /**
   * @description lazyKey 懒加载匹配的key
   */
  lazyKey: {
    type: String,
    default: 'id',
  },
  /**
   * @description itemSize
   */
  itemSize: {
    type: Number,
    default: 32,
  },
  /**
   * @description isDefaultQuery 是否默认查询
   */
  isDefaultQuery: {
    type: Boolean,
    default: true,
  },
  /**
   * @description showCheckbox 是否显示复选框
   */
  showCheckbox: {
    type: Boolean,
    default: false,
  },
  /**
   * @description forceOnSelectAll 是否强制开启全部
   */
  forceOnSelectAll: {
    type: Boolean,
    default: true,
  },
  /**
   * @description expandedLevel 如果defaultExpandAll为false，则展开的级别生效，并且默认为1级
   */
  expandedLevel: {
    type: Number,
    default: 1,
  },
  /**
   * @description showExpandIcon 是否展开搜索区域图标
   */
  showExpandIcon: {
    type: Boolean,
    default: true,
  },
} as const)

export const layoutTreeEmits = {
  'node-click': (data: TreeNodeData, node: TreeNode, e: MouseEvent) =>
    data && node && e,
  'result-back': (data: any) => data,
  check: (data: TreeNodeData, info: CheckedInfo) => data && info,
}

export type LayoutTreeProps = ExtractPropTypes<typeof layoutTreeProps>
export type LayoutTreeInstance = InstanceType<typeof LayoutTree>
export type LayoutTreeEmits = typeof layoutTreeEmits
